import React from 'react';
import { map } from 'lodash';
import styled from 'styled-components';
import { CustomDragLayer, ItemContainer } from './CustomDragLayer';
import { TransformComponent } from 'react-zoom-pan-pinch';
import { Board, BoardProps } from './Board';
import { tileSize, dndTypes, dragLayerRadius } from '../helpers/constants';
import { DropTargetMonitor, XYCoord } from 'react-dnd';
import { TileDropItem, TileGroupDropItem } from '../types';
import { useDrop } from 'react-dnd';
import { Tile } from './Tile';
import { ActiveDropTarget } from '../containers/ActiveDropTarget';
import { SelectedBoardTiles } from '../containers/SelectedBoardTiles';

const StyledBoard = styled(
    React.forwardRef<HTMLDivElement, BoardProps & { x?: number, y?: number, style?: any }>(
        ({ x, y, ...props }, ref) => <Board ref={ref} {...props} />,
    ),
).attrs((props) => {
    return props.x && props.y
        ? {
              style: {
                  transform: `translate(${props.x}px, ${props.y}px)`,
              },
          }
        : {};
})`
    overflow: auto;
`;

export type Position = {
    row: number,
    column: number,
};

type BoardWithDragLayerProps = BoardProps & {
    positionX: number,
    positionY: number,
    scale: number,
};

export const BoardWithDragLayer: React.FC<BoardWithDragLayerProps> = ({
    positionX,
    positionY,
    scale,
    onDrop,
    boardState,
    onClickTile,
    onDoubleClickTile,
}) => {
    const { setDropTarget } = ActiveDropTarget.useContainer();

    const getCoordinatesForClientOffset = (coordinate: { x: number, y: number }) => {
        const row = Math.ceil(-coordinate.y / tileSize);
        const column = Math.floor(coordinate.x / tileSize);

        return { row, column };
    };

    //const [coordinate, setCoordinate] = React.useState<Position | null>(null);

    const getCoordinateFromMonitor = (monitor: DropTargetMonitor) => {
        const itemType = monitor.getItemType();
        const clientOffset = monitor.getClientOffset();
        const initialClientOffset = monitor.getInitialClientOffset();
        const initialSourceClientOffset = monitor.getInitialSourceClientOffset();
        const deltaX =
            itemType === dndTypes.TILE_GROUP
                ? (initialClientOffset?.x || 0) - (initialSourceClientOffset?.x || 0)
                : 0;

        const deltaY =
            itemType === dndTypes.TILE_GROUP
                ? (initialClientOffset?.y || 0) - (initialSourceClientOffset?.y || 0)
                : 0;

        const x = ((clientOffset?.x || 0) - positionX) / scale - deltaX;
        const y = ((clientOffset?.y || 0) - positionY) / scale - deltaY;
        const coordinate = getCoordinatesForClientOffset({
            x,
            y,
        });

        return coordinate;
    };
    const [{ item, coordinate }, dropRef] = useDrop({
        accept: [dndTypes.TILE_FROM_HAND, dndTypes.TILE_FROM_BOARD, dndTypes.TILE_GROUP],
        drop: (item: TileDropItem, monitor) => {
            const coordinate = getCoordinateFromMonitor(monitor);
            //setCoordinate(null);
            if (monitor.isOver({ shallow: false })) {
                onDrop({ item, row: coordinate.row, column: coordinate.column });
            }
        },
        hover: () => {
            setDropTarget(dndTypes.BOARD);
        },
        collect: (monitor) => {
            const item: TileDropItem | TileGroupDropItem = monitor.getItem() || {};
            const coordinate = getCoordinateFromMonitor(monitor);
            const isOver = monitor.isOver();
            return {
                coordinate: isOver ? coordinate : null,
                item,
            };
        },
    });

    return (
        <>
            <CustomDragLayer
                component={(initialCoor, initialClientCoor, xyCoor) => {
                    // Render the board drag layer
                    if (coordinate) {
                        // Pretend that we grabbed the tile from the top left
                        const xDiff =
                            initialCoor && initialClientCoor
                                ? initialClientCoor.x - initialCoor.x
                                : 0;
                        const yDiff =
                            initialCoor && initialClientCoor
                                ? initialClientCoor.y - initialCoor.y
                                : 0;

                        const x = xyCoor
                            ? (positionX - xyCoor.x - xDiff) / scale + dragLayerRadius
                            : undefined;
                        const y = xyCoor
                            ? (positionY - xyCoor.y - yDiff) / scale + dragLayerRadius
                            : undefined;
                        return (
                            <ItemContainer windowWidth={window.innerWidth} currentOffset={xyCoor}>
                                <StyledBoard
                                    x={x}
                                    y={y}
                                    coordinate={coordinate}
                                    item={item}
                                    onDrop={onDrop}
                                    boardState={boardState}
                                    onClickTile={onClickTile}
                                    onDoubleClickTile={onDoubleClickTile}
                                />
                            </ItemContainer>
                        );
                    }
                }}
            />
            <TransformComponent>
                <StyledBoard
                    ref={dropRef}
                    coordinate={coordinate}
                    item={item}
                    onDrop={onDrop}
                    boardState={boardState}
                    onClickTile={onClickTile}
                    onDoubleClickTile={onDoubleClickTile}
                />
            </TransformComponent>
        </>
    );
};
